/* eslint-disable */
// /src/services/pointsService.js
/**
 * Points Service (Web)
 * - 페이지에서 Firestore 직접 접근하지 말고, 포인트 관련은 여기로만.
 *
 * ✅ Admin 지급 로직 SSOT에 맞춤
 * - points_balance/{memberId} : { balance:number, updatedAt }
 * - points_ledger            : 원장 (memberId 필수)
 *
 * ✅ memberId를 모르면 phoneE164로 members에서 memberId(docId)만 해석(resolve)해서 사용
 */

import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit as fsLimit,
    orderBy,
    query,
    startAfter,
    where,
    runTransaction,
    serverTimestamp,
} from "firebase/firestore";

import { db } from "./firebase";

function safeStr(v) {
    return String(v || "").trim();
}

function safeInt(v) {
    const n = Number(v);
    if (!Number.isFinite(n) || Number.isNaN(n)) return 0;
    return Math.trunc(n);
}

/**
 * ✅ members 문서 id를 모를 때(내 페이지에서) username/phoneE164로 찾아서 memberDocId를 리턴
 * - 우선순위: username -> phoneE164
 */
export async function resolveMemberDocId({ username = "", phoneE164 = "" } = {}) {
    if (!db) throw new Error("Firestore db is not initialized");

    const u = safeStr(username);
    const p = safeStr(phoneE164);

    const col = collection(db, "members");

    if (u) {
        const q1 = query(col, where("username", "==", u), fsLimit(1));
        const s1 = await getDocs(q1);
        if (!s1.empty) return s1.docs[0].id;
    }

    if (p) {
        const q2 = query(col, where("phoneE164", "==", p), fsLimit(1));
        const s2 = await getDocs(q2);
        if (!s2.empty) return s2.docs[0].id;
    }

    return "";
}

function pickMemberId({ memberId = "", memberDocId = "", phoneE164 = "", username = "" } = {}) {
    const mid = safeStr(memberId) || safeStr(memberDocId);
    const p = safeStr(phoneE164);
    const u = safeStr(username);
    return { mid, p, u };
}

async function resolveMidOrEmpty({ memberId = "", memberDocId = "", phoneE164 = "", username = "" } = {}) {
    const picked = pickMemberId({ memberId, memberDocId, phoneE164, username });
    let mid = picked.mid;

    if (!mid) {
        mid = await resolveMemberDocId({ username: picked.u, phoneE164: picked.p });
    }

    return safeStr(mid);
}

/**
 * ✅ 현재 잔액 조회 (points_balance/{memberId}.balance)
 * - 없으면 0
 */
export async function fetchMemberPointsBalance({
    memberId = "",
    memberDocId = "",
    phoneE164 = "",
    username = "",
} = {}) {
    if (!db) throw new Error("Firestore db is not initialized");

    const mid = await resolveMidOrEmpty({ memberId, memberDocId, phoneE164, username });
    if (!mid) return { memberId: "", balance: 0, updatedAt: null };

    const ref = doc(db, "points_balance", mid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        return { memberId: mid, balance: 0, updatedAt: null };
    }

    const data = snap.data() || {};
    return {
        memberId: mid,
        balance: safeInt(data.balance),
        updatedAt: data.updatedAt || null,
    };
}

/**
 * ✅ 내 포인트 이력(원장) 조회 (최신순)
 * - points_ledger where memberId == mid
 * - return: { items, nextCursor }
 *
 * cursor: { createdAt, id }
 */
export async function listMemberPointsLedger({
    memberId = "",
    memberDocId = "",
    phoneE164 = "",
    username = "",
    pageSize = 30,
    cursor = null,
    limitCount = null,
} = {}) {
    if (!db) throw new Error("Firestore db is not initialized");

    const mid = await resolveMidOrEmpty({ memberId, memberDocId, phoneE164, username });
    if (!mid) return { items: [], nextCursor: null };

    const n = limitCount != null ? safeInt(limitCount) : safeInt(pageSize);
    const size = n > 0 ? n : 30;

    const col = collection(db, "points_ledger");

    let q0 = query(
        col,
        where("memberId", "==", mid),
        orderBy("createdAt", "desc"),
        orderBy("__name__", "desc"),
        fsLimit(size)
    );

    if (cursor && cursor.createdAt && cursor.id) {
        q0 = query(
            col,
            where("memberId", "==", mid),
            orderBy("createdAt", "desc"),
            orderBy("__name__", "desc"),
            startAfter(cursor.createdAt, cursor.id),
            fsLimit(size)
        );
    }

    const snap = await getDocs(q0);
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

    const last = snap.docs[snap.docs.length - 1] || null;
    const nextCursor = last ? { createdAt: (last.data() || {}).createdAt || null, id: last.id } : null;

    return { items, nextCursor };
}

/**
 * (옵션) points_balance 스냅샷
 */
export async function getMemberPointsSnapshot({
    memberId = "",
    memberDocId = "",
    phoneE164 = "",
    username = "",
} = {}) {
    const res = await fetchMemberPointsBalance({ memberId, memberDocId, phoneE164, username });
    return { memberId: res.memberId, pointsBalance: safeInt(res.balance) };
}

/**
 * ✅ (호환) 예전 화면이 찾던 export가 있으면 에러나니까 alias로 제공
 * - fetchMyPointsHistory({ phoneE164, memberDocId, limitCount }) 형태로도 사용 가능
 */
export async function fetchMyPointsHistory({
    phoneE164 = "",
    memberId = "",
    memberDocId = "",
    limitCount = 200,
} = {}) {
    const res = await listMemberPointsLedger({
        phoneE164,
        memberId,
        memberDocId,
        limitCount,
    });
    return res.items || [];
}

/**
 * ✅ (추가) memberId 기준 포인트 이력 조회 (관리자/유틸)
 */
export async function fetchPointsHistoryByMember({ memberId = "", limit: limitCount = 200 } = {}) {
    const mid = safeStr(memberId);
    if (!mid) throw new Error("memberId가 필요합니다.");

    const col = collection(db, "points_ledger");
    const q0 = query(
        col,
        where("memberId", "==", mid),
        orderBy("createdAt", "desc"),
        orderBy("__name__", "desc"),
        fsLimit(safeInt(limitCount) > 0 ? safeInt(limitCount) : 200)
    );

    const snap = await getDocs(q0);
    const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
    return { rows };
}

/**
 * ✅ 포인트 부여/차감 (트랜잭션)
 *
 * - memberId를 모르면: memberDocId/username/phoneE164로 resolve 후 사용
 * - points_balance/{memberId}.balance 업데이트 + points_ledger 원장 기록
 *
 * payload 예:
 * {
 *   memberId?, memberDocId?, phoneE164?, username?,
 *   delta: number (+/-),
 *   reasonType?: "ADMIN_ADJUST" | "ORDER_USE" | ...,
 *   reason: string,
 *   actorRole: "admin" | "user" | "system",
 *   actorAdminId?: string,   // admin일 때
 *   actorId?: string,        // user/system일 때 (ex: phoneE164)
 *   source?: "admin" | "order" | ...,
 *   type?: "grant" | "deduct",
 *   orderId?: string
 * }
 */
export async function applyPointsChange(payload = {}) {
    if (!db) throw new Error("Firestore db is not initialized");

    const memberId = safeStr(payload.memberId);
    const memberDocId = safeStr(payload.memberDocId);
    const phoneE164 = safeStr(payload.phoneE164);
    const username = safeStr(payload.username);

    const mid = await resolveMidOrEmpty({ memberId, memberDocId, phoneE164, username });
    if (!mid) throw new Error("memberId를 해석할 수 없습니다.");

    const delta = safeInt(payload.delta);
    if (!delta || delta === 0) throw new Error("delta는 0이 될 수 없습니다.");

    const reason = safeStr(payload.reason);
    if (!reason) throw new Error("reason이 필요합니다.");

    const type = safeStr(payload.type) || (delta < 0 ? "deduct" : "grant");
    const source = safeStr(payload.source) || "admin";
    const actorRole = safeStr(payload.actorRole) || "admin";
    const orderTitleSnapshot = safeStr(payload.orderTitleSnapshot);

    const actorAdminId = safeStr(payload.actorAdminId);
    const actorId = safeStr(payload.actorId);

    if (actorRole === "admin") {
        if (!actorAdminId) throw new Error("actorAdminId가 필요합니다.");
    } else {
        if (!actorId) throw new Error("actorId가 필요합니다.");
    }

    const reasonTypeFromPayload = safeStr(payload.reasonType);
    const reasonType = reasonTypeFromPayload || (source === "order" ? "ORDER_USE" : "ADMIN_ADJUST");
    const orderId = safeStr(payload.orderId);

    const balanceRef = doc(db, "points_balance", mid);
    const ledgerRef = doc(collection(db, "points_ledger"));

    await runTransaction(db, async (tx) => {
        const balSnap = await tx.get(balanceRef);
        const before = balSnap.exists() ? safeInt((balSnap.data() || {}).balance) : 0;
        const after = before + delta;

        if (after < 0) {
            throw new Error("차감 후 포인트가 0 미만이 될 수 없습니다.");
        }

        tx.set(
            balanceRef,
            {
                memberId: mid,
                balance: after,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );

        tx.set(ledgerRef, {
            memberId: mid,
            phoneE164: phoneE164 || "",

            delta,
            balanceBefore: before,
            balanceAfter: after,

            type,
            reasonType,
            reason,

            orderId: orderId || "",

            actorRole,
            actorAdminId: actorRole === "admin" ? actorAdminId : "",
            actorId: actorRole === "admin" ? "" : actorId,

            source,
            orderTitleSnapshot,

            createdAt: serverTimestamp(),
            createdAtMs: Date.now(),
        });
    });

    return { ok: true, memberId: mid };
}
